"use client";

import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// Tiny local helper replacing shadcn's `cn` (clsx + tailwind-merge) —
// avoids needing the "@/lib/utils" alias, which this project doesn't have set up.
const cn = (...classes) => classes.filter(Boolean).join(" ");

const ActionButton = ({ children, href }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    className="inline-block mt-8"
  >
    <Link
      to={href || "#"}
      className="block px-8 py-3 rounded-full bg-red-500 text-white font-semibold shadow-lg transition-colors hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75"
    >
      {children}
    </Link>
  </motion.div>
);

export const AnimatedMarqueeHero = ({
  tagline,
  title,
  description,
  ctaText,
  ctaHref,
  images,
  className,
}) => {
  const FADE_IN_ANIMATION_VARIANTS = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 20 } },
  };

    // Shuffle once per mount so the reel order isn't the same every load,
  // then duplicate the shuffled order for the seamless scroll loop.
  const shuffledImages = React.useMemo(() => {
    const arr = [...images];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [images]);

  const duplicatedImages = [...shuffledImages, ...shuffledImages];

  return (
        <section
      className={cn(
        "relative w-full min-h-[100svh] overflow-hidden bg-white flex flex-col items-center justify-start pt-24 sm:pt-28 md:justify-center md:pt-0 text-center px-4",
        className
      )}
    >
            <div className="z-10 flex flex-col items-center pb-16 md:pb-24">
        <motion.div
          initial="hidden"
          animate="show"
          variants={FADE_IN_ANIMATION_VARIANTS}                    className="mt-14 sm:mt-20 md:mt-28 mb-4 inline-block rounded-full border border-border bg-white px-4 py-1.5 text-sm font-medium text-muted-foreground shadow-sm"
          
        >
          {tagline}
        </motion.div>

        <motion.h1
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.1,
              },
            },
          }}
            className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter leading-tight text-black"
        >
          {typeof title === "string" ? (
            title.split(" ").map((word, i) => (
              <motion.span
                key={i}
                variants={FADE_IN_ANIMATION_VARIANTS}
                className="inline-block"
              >
                {word}&nbsp;
              </motion.span>
            ))
          ) : (
            title
          )}
        </motion.h1>

        <motion.p
          initial="hidden"
          animate="show"
          variants={FADE_IN_ANIMATION_VARIANTS}
          transition={{ delay: 0.5 }}
          className="mt-6 max-w-xl text-lg text-gray-700"
        >
          {description}
        </motion.p>

        <motion.div
          initial="hidden"
          animate="show"
          variants={FADE_IN_ANIMATION_VARIANTS}
          transition={{ delay: 0.6 }}
        >
                    <ActionButton href={ctaHref}>{ctaText}</ActionButton>
        </motion.div>
      </div>

            <div className="absolute bottom-0 left-0 w-full h-1/5 md:h-1/4 [mask-image:linear-gradient(to_bottom,transparent,black_20%,black_80%,transparent)]">
                <motion.div
          className="flex gap-4 will-change-transform transform-gpu"
          animate={{
            x: ["-100%", "0%"],
            transition: {
              ease: "linear",
              duration: 40,
              repeat: Infinity,
            },
          }}
        >
          {duplicatedImages.map((src, index) => (
            <div
              key={index}
              className="relative aspect-[3/4] h-48 md:h-64 flex-shrink-0"
              style={{
                rotate: `${index % 2 === 0 ? -2 : 5}deg`,
              }}
            >
                            <img
                src={src}
                alt={`Showcase image ${index + 1}`}
                loading={index < 8 ? "eager" : "lazy"}
                decoding="async"
                className="w-full h-full object-cover rounded-2xl shadow-md"
              />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};